import { useEffect, useMemo, useState } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import { Container } from "react-bootstrap";
import PageLoading from "components/PageLoading";

import PaymentTable from "./PaymentTable/PaymentTable";
import PaginationControls from "../Documentation/Analysis/ComponentsAnalysis/PaginationControls/PaginationControls";
import SearchExport from "../Documentation/Analysis/ComponentsAnalysis/SearchExport/SearchExport";
import FilterSectionPayment from "./FilterSectionPayment/FilterSectionPayment";
import HistoryPayment from "./HistoryPayment/HistoryPayment";
import "./Payment.css";
import {
  getDespesasExtratosAvaliacoesLegacy,
  getDespesasExtratosAvaliacoesSolicitacoes,
  getDespesasExtratosAvaliacaoEventos,
  getHistoricoAcaoComprovanteDespesa,
} from "../../services/FinanceiroService";
import {
  mapComprovantesSolicitacoesToPaymentRows,
  type PaymentListRow,
} from "./V2/mapComprovantesSolicitacoesToPaymentRows";
import {
  mapDespesaExtratoEventosToHistoryRows,
  type HistoryPaymentRow,
} from "./V2/mapDespesaExtratoEventosToHistoryRows";
import { getAnoRequestParam } from "../Documentation/Extract/V2/anoRequestParam";
import { formatDateTimeBR } from "../../utils/date";
import { normalizeText } from "../../utils";

const onlyDigits = (txt: unknown) =>
  String(txt || "").replace(/\D/g, "");

const STORAGE_KEY = "payment_filters_v3";

const ALL_SITUATIONS = [
  "Comprovantes pendentes",
  "Conferência pendente",
  "Conferência em andamento",
  "Correção pendente",
  "Assinatura pendente",
  "Assinatura expirada",
  "Assinatura Concluída",
];

type SortConfig = {
  key: string | null;
  direction: "asc" | "desc" | null;
};

type StoredFilters = {
  selectedSituation?: string[];
  searchTerm?: string;
  selectedYear?: number;
  currentPage?: number;
  itemsPerPage?: number;
  sortConfig?: SortConfig;
};

const readStoredFilters = (): StoredFilters => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
};

const Payment = () => {
  const stored = readStoredFilters();

  const [currentPage, setCurrentPage] = useState(
    Number.isFinite(stored.currentPage) ? Number(stored.currentPage) : 1,
  );
  const [itemsPerPage, setItemsPerPage] = useState(
    Number.isFinite(stored.itemsPerPage) ? Number(stored.itemsPerPage) : 10,
  );
  const [searchTerm, setSearchTerm] = useState(
    typeof stored.searchTerm === "string" ? stored.searchTerm : "",
  );
  const [sortConfig, setSortConfig] = useState<SortConfig>(
    stored.sortConfig && typeof stored.sortConfig === "object"
      ? stored.sortConfig
      : { key: null, direction: null },
  );
  const [selectedYear, setSelectedYear] = useState(
    typeof stored.selectedYear === "number" ? stored.selectedYear : 2026,
  );
  const [availableYears, setAvailableYears] = useState<number[]>([2026, 2025]);

  const [selectedSituation, setSelectedSituation] = useState<string[]>(() => {
    if (Array.isArray(stored.selectedSituation)) {
      const sanitized = stored.selectedSituation.filter(
        (s): s is string =>
          typeof s === "string" && ALL_SITUATIONS.includes(s),
      );
      if (sanitized.length > 0) return sanitized;
    }
    return [
      "Comprovantes pendentes",
      "Conferência pendente",
      "Correção pendente",
      "Conferência em andamento",
      "Assinatura pendente",
      "Assinatura expirada",
    ];
  });

  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [historySindicatoId, setHistorySindicatoId] = useState<number | null>(
    null,
  );
  const [historyDataExtract, setHistoryDataExtract] = useState<
    HistoryPaymentRow[]
  >([]);
  const [rows, setRows] = useState<PaymentListRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          selectedSituation,
          searchTerm,
          selectedYear,
          currentPage,
          itemsPerPage,
          sortConfig,
        }),
      );
    } catch {
      /* ignore */
    }
  }, [
    selectedSituation,
    searchTerm,
    selectedYear,
    currentPage,
    itemsPerPage,
    sortConfig,
  ]);

  const extractAnosModelo = (
    response:
      | { avaliacoesModelo?: { anoReferencia: number }[] }
      | null
      | undefined,
  ): number[] => {
    const anos =
      response?.avaliacoesModelo?.map((m) => m.anoReferencia) ?? [];
    return [...new Set(anos)].sort((a, b) => b - a);
  };

  useEffect(() => {
    const fetchRows = async () => {
      setLoading(true);
      try {
        if (selectedYear === 2025) {
          const response = await getDespesasExtratosAvaliacoesLegacy();
          setRows(
            mapComprovantesSolicitacoesToPaymentRows(
              response?.comprovantes ?? [],
              selectedYear,
            ),
          );
        } else {
          const response = await getDespesasExtratosAvaliacoesSolicitacoes(
            getAnoRequestParam(selectedYear),
          );
          const anosModelo = extractAnosModelo(response);
          if (anosModelo.length > 0) {
            const withLegacy = anosModelo.includes(2025)
              ? anosModelo
              : [...anosModelo, 2025].sort((a, b) => b - a);
            setAvailableYears(withLegacy);
          }
          setRows(
            mapComprovantesSolicitacoesToPaymentRows(
              response?.comprovantes ?? [],
              selectedYear,
            ),
          );
        }
      } catch (e) {
        console.error("❌ Erro ao buscar comprovantes:", e);
        setRows([]);
      } finally {
        setLoading(false);
      }
    };

    fetchRows();
  }, [selectedYear]);

  const filteredData = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    const termDigits = onlyDigits(searchTerm);
    const selectedNorm = selectedSituation.map((s) => normalizeText(s));

    return rows.filter((item) => {
      if (normalizeText(item.situacao) === "inapto") return false;

      const situacaoOk =
        selectedSituation.length === 0
          ? false
          : selectedSituation.length === ALL_SITUATIONS.length
            ? true
            : selectedNorm.includes(normalizeText(item.situacao));

      if (!situacaoOk) return false;
      if (!term) return true;

      const sigla = String(item.sigla || "").toLowerCase();
      const nome = String(item.nome || "").toLowerCase();
      const cnpj = onlyDigits(item.cnpj);
      const solicitacao = String(item.solicitacaoLabel || "").toLowerCase();

      return (
        sigla.includes(term) ||
        nome.includes(term) ||
        solicitacao.includes(term) ||
        (termDigits.length > 0 && cnpj.includes(termDigits))
      );
    });
  }, [rows, searchTerm, selectedSituation]);

  const sortedData = useMemo(() => {
    if (!sortConfig.key || !sortConfig.direction) return filteredData;

    const key = sortConfig.key;
    const dir = sortConfig.direction === "asc" ? 1 : -1;
    const dateKeys = new Set([
      "envio",
      "solicitacaoCorrecao",
      "correcao",
      "envioFormulario",
    ]);

    return [...filteredData].sort((a, b) => {
      const av = (a as Record<string, unknown>)[key];
      const bv = (b as Record<string, unknown>)[key];

      if (dateKeys.has(key)) {
        const at = av ? new Date(String(av)).getTime() : 0;
        const bt = bv ? new Date(String(bv)).getTime() : 0;
        return (at - bt) * dir;
      }

      return String(av ?? "").localeCompare(String(bv ?? ""), "pt-BR") * dir;
    });
  }, [filteredData, sortConfig]);

  const totalPages = Math.ceil(sortedData.length / itemsPerPage) || 1;
  const currentItems = sortedData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );
  const startResult =
    sortedData.length === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
  const endResult = Math.min(currentPage * itemsPerPage, sortedData.length);

  const handleSort = (key: string) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        const direction =
          prev.direction === "asc"
            ? "desc"
            : prev.direction === "desc"
              ? null
              : "asc";
        return { key: direction ? key : null, direction };
      }
      return { key, direction: "asc" };
    });
  };

  const getSortIcon = (key: string) => {
    if (sortConfig.key !== key) return "↕";
    return sortConfig.direction === "asc" ? "▲" : "▼";
  };

  const handleSituationFilter = (value: string | string[]) => {
    if (Array.isArray(value)) {
      setSelectedSituation(value);
      setCurrentPage(1);
      return;
    }
    setSelectedSituation((prev) =>
      prev.includes(value) ? prev.filter((p) => p !== value) : [...prev, value],
    );
    setCurrentPage(1);
  };

  const exportData = sortedData.map((item) => ({
    CNPJ: item.cnpj,
    SIGLA: item.sigla,
    SOLICITACAO: item.solicitacaoLabel || "-",
    SITUACAO: item.situacao || "-",
    ENVIO_COMPROVANTE: formatDateTimeBR(item.envio),
    SOLICITACAO_CORRECAO: formatDateTimeBR(item.solicitacaoCorrecao),
    CORRECAO_COMPROVANTE: formatDateTimeBR(item.correcao),
    ENVIO_FORMULARIO: formatDateTimeBR(item.envioFormulario),
  }));

  const handleOpenHistory = async (item: PaymentListRow) => {
    const extratoId = item.extratoId ?? item.solicitacaoId ?? null;
    const isV2 = selectedYear > 2025 && extratoId != null;
    const historicoId = item.idSindicato ?? item.id;
    setHistorySindicatoId(historicoId);

    try {
      if (isV2) {
        const eventos = await getDespesasExtratosAvaliacaoEventos(extratoId);
        setHistoryDataExtract(mapDespesaExtratoEventosToHistoryRows(eventos));
        setShowHistoryModal(true);
        return;
      }

      const resp = await getHistoricoAcaoComprovanteDespesa(historicoId);
      const lista = Array.isArray(resp) ? resp : [];
      const formatted: HistoryPaymentRow[] = lista.map(
        (
          entry: {
            acao?: string;
            titulo?: string;
            data?: string;
            dataHora?: string;
            id?: number;
          },
          i: number,
        ) => ({
          acao: entry.acao ?? entry.titulo ?? "-",
          data: entry.data ?? entry.dataHora ?? null,
          id: entry.id ?? i,
          arquivoId: null,
        }),
      );
      setHistoryDataExtract(formatted);
      setShowHistoryModal(true);
    } catch (error) {
      console.error("❌ Erro ao carregar histórico:", error);
      setHistoryDataExtract([]);
      setShowHistoryModal(true);
    }
  };

  if (loading) return <PageLoading />;

  return (
    <Container fluid className="payment-container lista-sindicatos-page">
      <h1 className="payment-title">Lista de Sindicatos</h1>

      <div className="filters-actions-container">
        <div className="filters-search-row">
          <FilterSectionPayment
            selectedSituation={selectedSituation}
            handleSituationFilter={handleSituationFilter}
          />

          <SearchExport
            selectedYear={selectedYear}
            setSelectedYear={setSelectedYear}
            availableYears={availableYears}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            exportData={exportData}
            fileName={`ListaComprovantesAnalista_${selectedYear}`}
          />
        </div>

        <PaymentTable
          currentItems={currentItems}
          handleSort={handleSort}
          getSortIcon={getSortIcon}
          onOpenHistory={handleOpenHistory}
          selectedYear={selectedYear}
        />
      </div>

      <div className="pagination-wrapper">
        <PaginationControls
          currentPage={currentPage}
          totalPages={totalPages}
          setCurrentPage={setCurrentPage}
          itemsPerPage={itemsPerPage}
          setItemsPerPage={setItemsPerPage}
          startResult={startResult}
          endResult={endResult}
          totalItems={sortedData.length}
        />

        <HistoryPayment
          show={showHistoryModal}
          onHide={() => setShowHistoryModal(false)}
          idSindicato={historySindicatoId}
          historyDataExtract={historyDataExtract}
        />
      </div>
    </Container>
  );
};

export default Payment;
